import { FC, ReactElement, forwardRef, useCallback } from 'react';
import { UserType } from 'api/models';
import { Project } from 'api/project';
import { RbacPermissionRule } from 'api/rbac/rbac-types';
import { useUser } from 'hooks/use-user';
import type { UseWithAccess } from './useWithAccess';
import { ProjectResourceOrUndefined, useWithAccess } from './useWithAccess';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export function withAccess<P extends any>(
  WrappedComponent: React.ComponentType<React.PropsWithChildren<P>>,
) {
  const WithAccess = forwardRef<unknown, P & UseWithAccess>((props, ref) => {
    const {
      rule,
      resourceData,
      propsIfNoAccess,
      children,
      actionScope,
      projectId,
    } = props;
    const { hasAccessToResource } = useWithAccess();
    const hasAccess = hasAccessToResource({
      rule,
      resourceData,
      actionScope,
      projectId,
    });

    if (!hasAccess) {
      return propsIfNoAccess ? (
        <WrappedComponent
          ref={ref as React.Ref<unknown>}
          hasAccess={hasAccess}
          actionScope={actionScope}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(props as P & object)}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(propsIfNoAccess as P & object)}
        >
          {children}
        </WrappedComponent>
      ) : null;
    }

    return (
      <WrappedComponent
        ref={ref as React.Ref<unknown>}
        hasAccess={hasAccess}
        actionScope={actionScope}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(props as P & object)}
      >
        {children}
      </WrappedComponent>
    );
  });

  const name =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  WithAccess.displayName = `withAccess(${name})`;

  return WithAccess;
}

export const HideIfNotAllowed: FC<React.PropsWithChildren<UseWithAccess>> = ({
  children,
  ...rest
}) => {
  const userData = useUser();
  const { hasAccessToResource } = useWithAccess();

  if (
    userData?.user?.userType === UserType.CustomerPortalBot &&
    rest?.ignoreForBotUsers
  ) {
    return <>{children}</>;
  }

  return hasAccessToResource(rest) ? <>{children}</> : <></>;
};

type SwitchContentBasedOnAccessProps = UseWithAccess & {
  hasAccess: ReactElement;
  doesNotHaveAccess: ReactElement;
};

export const SwitchContentBasedOnAccess: FC<
  React.PropsWithChildren<SwitchContentBasedOnAccessProps>
> = ({ hasAccess, doesNotHaveAccess, ...rest }) => {
  const { hasAccessToResource } = useWithAccess();

  return hasAccessToResource(rest) ? hasAccess : doesNotHaveAccess;
};

export const useCheckRequestScopeForProject = (project?: Project) => {
  const { hasAccessToResource, getRequestScopeForProjectResource } =
    useWithAccess();
  const userData = useUser();
  const projectResource = ProjectResourceOrUndefined(project);

  const hasRequestScopeAccess = useCallback(
    ({ hasData, rule }: { rule: RbacPermissionRule; hasData: boolean }) => {
      const hasAccessToTheResource = hasAccessToResource({
        rule,
        resourceData: projectResource,
      });

      const requestScope = getRequestScopeForProjectResource({
        rule,
        resourceData: projectResource,
      });

      if (hasAccessToTheResource && requestScope === 'RESTRICTED') {
        return hasData;
      }
      if (hasAccessToTheResource && requestScope === 'PROJECT_OWNER') {
        return project?.projectOwner?.userId === userData?.user?.userId;
      }

      return hasAccessToTheResource;
    },
    [
      getRequestScopeForProjectResource,
      hasAccessToResource,
      projectResource,
      project?.projectOwner?.userId,
      userData?.user?.userId,
    ],
  );

  return { hasRequestScopeAccess };
};
